import type { MessageDescriptor } from '@lingui/core'
import { useLingui } from '@lingui/react/macro'
import { createLink, Link } from '@tanstack/react-router'
import CantileverSymbol from 'assets/cantilever-symbol.svg?react'
import CantileverWordmark from 'assets/cantilever-wordmark.svg?react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import type { WorkspaceSettingsNavigateHandler } from 'features/navbar/configSettings/ConfigSettingsSidebarMenuItems'
import React, { useCallback } from 'react'
import {
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import type { FileRouteTypes } from 'routeTree.gen'
import styled from 'styled-components'

const BrandLinkStyles = styled(Link)<{ $collapsed: boolean }>`
  display: flex;
  width: ${({ $collapsed }) => ($collapsed ? '2rem' : '100%')};
  height: 2rem;
  align-items: center;
  gap: 0.5rem;
  overflow: hidden;
  padding: 0;
  border-radius: var(--radius);
  color: var(--foreground);
  text-decoration: none;

  &:hover {
    background-color: var(--accent);
  }

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: -2px;
  }
`

const BrandLink = createLink(BrandLinkStyles)

const BrandSymbolSlot = styled.span`
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
`

const BrandSymbol = styled(CantileverSymbol)`
  width: 1.5rem;
  height: auto;
  flex-shrink: 0;

  path {
    fill: currentColor;
    fill-opacity: 1;
  }
`

const BrandWordmark = styled(CantileverWordmark)`
  width: auto;
  height: 0.833rem;
  flex-shrink: 0;
  margin-top: -2px;

  path {
    fill: currentColor;
  }
`

export const EmbeddedSidebarBrandHomeLink: React.FC<{
  to: FileRouteTypes['to']
  destinationKey: string
  label: MessageDescriptor
  organizationId: string
  siteId: string
  isCollapsed: boolean
  onWorkspaceNavigate: WorkspaceSettingsNavigateHandler
}> = ({
  to,
  destinationKey,
  label,
  organizationId,
  siteId,
  isCollapsed,
  onWorkspaceNavigate,
}) => {
  const { t, i18n } = useLingui()
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      onWorkspaceNavigate(event, {
        destinationKey,
        label: i18n._(label),
      })
    },
    [destinationKey, i18n, label, onWorkspaceNavigate]
  )

  const link = (
    <BrandLink
      to={to}
      search={{
        [SEARCH_PARAM_ORG_ID]: organizationId,
        [SEARCH_PARAM_SITE_ID]: siteId,
      }}
      $collapsed={isCollapsed}
      aria-label={t`Go to Home`}
      onClick={handleClick}>
      <BrandSymbolSlot>
        <BrandSymbol aria-hidden='true' />
      </BrandSymbolSlot>
      {!isCollapsed ? (
        <BrandWordmark aria-hidden='true' data-testid='cantilever-wordmark' />
      ) : null}
    </BrandLink>
  )

  if (!isCollapsed) return link

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side='right' align='center'>
        {t`Go to Home`}
      </TooltipContent>
    </Tooltip>
  )
}
